import {Timeline} from '@mantine/core';
import {MdArrowForwardIos, MdOutlineStickyNote2} from 'react-icons/md';
import {COLOR_BLACK_2} from '../../../../../../config/constants/colorCodes';
import {ENTRY_MANAGEMENTS_PROGRESS} from '../../../../../../config/constants/routes';
import type {EntryManagementSummaryType} from '../../../../../../core/repositories/mypageEntryManagementsRepository';
import {cn} from '../../../../../../lib/classname';
import {CommonLink} from '../../../../../../shared/components/CommonLink';
import {formatDateWithTime} from '../../../../../../shared/services/formatDate';
import {OVERALL_STATUS} from '../../../../constants';
import {CompanyNameText} from './CompanyNameText';
import {DeadlineText} from './DeadlineText';
import {EventDateText} from './EventDateText';
import {PlaceText} from './PlaceText';
import {EntryStatusLabel} from './ProgressCardStatusBadge';

type Props = {
  entry: EntryManagementSummaryType;
  isSelected?: boolean;
  /** SP版でのみ未エントリーラベルを表示 */
  isSp?: boolean;
  /** falseを返すと詳細への遷移を中断する */
  canOpenDetail?: () => boolean;
};

type StepProps = {
  entry: EntryManagementSummaryType;
};

const NoStep: React.FC<StepProps> = ({entry}) => {
  const formattedCreated = formatDateWithTime(entry.created);
  if (entry.overallStatus === OVERALL_STATUS.ACTIVE) {
    return (
      <div className="flex flex-col text-sm p-8">
        {entry.hasCompletedEntry ? (
          <>
            {formattedCreated && (
              <p>{formattedCreated}にエントリーしました。</p>
            )}
            <p>選考結果を更新、または次の選考予定を追加してください。</p>
          </>
        ) : (
          <>
            <p>次の選考予定が未設定です。</p>
            <p>選考結果を更新、または次の選考予定を追加してください。</p>
          </>
        )}
      </div>
    );
  }

  if (entry.overallStatus === OVERALL_STATUS.COMPLETED) {
    return <p className="text-base leading p-8">内定おめでとうございます！</p>;
  }

  return null;
};

export const ProgressCard: React.FC<Props> = ({
  entry,
  isSelected,
  isSp = false,
  canOpenDetail,
}) => {
  const latestSteps = entry.latestSteps;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (canOpenDetail && !canOpenDetail()) {
      event.preventDefault();
    }
  };

  return (
    <CommonLink
      href={`${ENTRY_MANAGEMENTS_PROGRESS}?id=${entry.id}`}
      onClick={handleClick}
      className={cn(
        'flex flex-col p-16 rounded-16 w-full min-h-[110px] gap-4 bg-white',
        'transition-all duration-200',
        'hover:shadow-md hover:scale-[1.01]',
        {
          'border-2 border-solid border-primary shadow-md': isSelected,
        },
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-8">
          {isSp && (
            <EntryStatusLabel
              hasCompletedEntry={entry.hasCompletedEntry ?? false}
            />
          )}
          <CompanyNameText companyName={entry.companyName} />
        </div>
        <div>
          <MdArrowForwardIos />
        </div>
      </div>
      <div className="flex gap-8 justify-between items-center flex-1">
        {latestSteps && latestSteps.length > 0 ? (
          <Timeline
            active={0}
            bulletSize={8}
            lineWidth={2}
            sx={{
              '.mantine-Timeline-item:not(:first-of-type)': {
                marginTop: '8px',
              },
            }}
          >
            {latestSteps.map((step, index) => (
              <Timeline.Item
                key={step.id}
                bullet={<div className="rounded-full" />}
                title={
                  <p className="flex items-center gap-4">
                    {step.stepName}{' '}
                    {step.notes && (
                      <MdOutlineStickyNote2 fill={COLOR_BLACK_2} size={12} />
                    )}
                  </p>
                }
                lineVariant={
                  index === latestSteps.length - 1 ? 'dashed' : 'solid'
                }
                className="text-sm"
              >
                <div className="text-xs">
                  <DeadlineText
                    deadline={step.deadline ?? ''}
                    hasResult={!!step.result}
                  />
                  <EventDateText
                    startDate={step.startDate ?? ''}
                    endDate={step.endDate ?? ''}
                    hasResult={!!step.result}
                  />
                  <PlaceText place={step.place ?? ''} />
                </div>
              </Timeline.Item>
            ))}
            <Timeline.Item />
          </Timeline>
        ) : (
          <NoStep entry={entry} />
        )}
      </div>
    </CommonLink>
  );
};
